import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ApiService } from '../services/api.service';
import { HumanizeEnumPipe } from '../pipes/humanize-enum.pipe';

interface SalesOrder {
  id: number;
  number?: string;
  date?: Date;
  customerId?: number;
  customerName?: string;
  customerPO?: string;
  status?: string;
  totalAmount?: number;
  salesTax?: number;
  shipFee?: number;
  handlingFee?: number;
}

@Component({
  standalone: true,
  selector: 'app-salesorders',
  templateUrl: './salesorders.component.html',
  styleUrl: './salesorders.component.css',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    HumanizeEnumPipe
  ]
})
export class SalesOrdersComponent implements OnInit {
  dataSource = new MatTableDataSource<SalesOrder>([]);
  displayedColumns: string[] = ['number', 'date', 'customerName', 'customerPO', 'status', 'totalAmount', 'copy'];
  loading: boolean = true;
  error: string | null = null;
  copyingOrderId: number | null = null;

  @ViewChild(MatSort) set matSort(s: MatSort) {
    if (s) { this.dataSource.sort = s; }
  }

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    if (p) { this.dataSource.paginator = p; }
  }

  constructor(
    private router: Router,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadSalesOrders();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  loadSalesOrders(): void {
    this.loading = true;
    this.error = null;
    
    this.apiService.get<any>('salesorder/all').subscribe({
      next: (data) => {
        let ordersArray: any[];
        if (Array.isArray(data)) {
          ordersArray = data;
        } else if (data && data.salesOrders) {
          ordersArray = data.salesOrders;
        } else if (data && data.data) {
          ordersArray = data.data;
        } else if (data && data.value) {
          ordersArray = data.value;
        } else {
          ordersArray = [];
        }
        
        const salesOrders = ordersArray.map(so => ({
          id: so.id || so.Id,
          number: so.number || so.Number,
          date: so.date || so.Date,
          customerId: so.customerId || so.CustomerId,
          customerName: so.customer?.name || so.Customer?.Name || so.customerName || so.CustomerName || '',
          customerPO: so.customerPO || so.CustomerPO,
          status: so.status || so.Status,
          totalAmount: so.totalAmount || so.TotalAmount || 0,
          salesTax: so.salesTax || so.SalesTax,
          shipFee: so.shipFee || so.ShipFee,
          handlingFee: so.handlingFee || so.HandlingFee
        }));
        
        this.dataSource.data = salesOrders;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading sales orders:', err);
        
        if (err.status === 0) {
          this.error = 'Cannot connect to API. Make sure the Web API is running.';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please log out and log in again.';
        } else if (err.status === 404) {
          this.error = 'API endpoint not found.';
        } else {
          this.error = `Failed to load sales orders: ${err.message || 'Unknown error'}`;
        }
        
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  createNewOrder(): void {
    this.router.navigate(['/orders/new']);
  }

  viewOrder(order: SalesOrder): void {
    this.router.navigate(['/orders', order.id]);
  }

  editOrder(order: SalesOrder): void {
    this.router.navigate(['/orders', order.id]);
  }

  copyOrder(order: SalesOrder, event: Event): void {
    event.stopPropagation();
    this.copyingOrderId = order.id;

    this.apiService.get<any>(`salesorder/${order.id}`).subscribe({
      next: (data) => {
        this.copyingOrderId = null;
        this.router.navigate(['/orders/new'], { state: { prefill: data, copySalesOrder: true } });
      },
      error: (err) => {
        console.error('Error copying sales order:', err);
        this.error = `Failed to copy sales order: ${err.message || 'Unknown error'}`;
        this.copyingOrderId = null;
        this.cdr.detectChanges();
      }
    });
  }
}
